/**
 * The formula-injection guard for lexicon CSVs, and its inverse (VE-24948 / U5).
 *
 * Both halves live here because they are ONE rule read in two directions, and
 * splitting them is what broke the round trip (review nit N1). `csvExport`
 * prefixed an apostrophe to any cell that a spreadsheet would evaluate as a
 * formula; `csvValidator` never took it off. So a term beginning `=`, `+`, `-`
 * or `@` — and `-` is not exotic, plenty of case numbers and account fragments
 * start with one — came back from an export/import round trip as a DIFFERENT
 * term, matching nothing it used to match. A silent under-redaction, produced
 * by the app on its own file.
 *
 * WHY THE GUARD EXISTS AT ALL. Terms are author-written and land in a file a
 * reviewer opens in Excel, Sheets or LibreOffice, where a leading `=`, `+`,
 * `-`, `@`, tab or CR makes the cell a live formula. `Papa.unparse` quotes
 * separators; it does not neutralise formulas. The leading apostrophe is the
 * spreadsheet convention for "this is text", and the trigger is tested past
 * leading whitespace because LibreOffice evaluates `" =cmd|..."` just as
 * readily.
 *
 * WHY THE GUARD ALSO ESCAPES ITSELF. A cell that ALREADY starts with an
 * apostrophe is prefixed too, even though it needs no neutralising. Without
 * that the pair is not invertible and the bug simply moves: a term the author
 * wrote as `'=x` is not a formula, so it went out unguarded, and the importer
 * — seeing an apostrophe in front of a trigger — would take it off and import
 * `=x`. Escaping the escape costs one character in the file and makes every
 * term round-trip exactly: `'tis` writes as `''tis` and reads back as `'tis`.
 *
 * The one genuinely ambiguous input is a HAND-WRITTEN cell holding an
 * apostrophe in front of a trigger, since nothing in the file says whether the
 * author meant it as a guard. It resolves as a guard, which is the common
 * reading by a wide margin. Every file this app writes is unambiguous.
 *
 * SCOPE. Lexicon CSVs only. `redactionReportCsv` and `auditLogCsv` each carry
 * their own copy of the trigger, and `auditLogCsv`'s is weaker (review nit
 * N16); unifying all three is a separate change to three call sites with three
 * sets of tests, not a drive-by here.
 */

/** A cell a spreadsheet would evaluate rather than display. */
const FORMULA_TRIGGER = /^\s*[=+\-@]|^[\t\r\n]/;

/** True when a leading apostrophe on this cell would be one WE wrote. */
const isGuarded = (rest: string): boolean =>
  FORMULA_TRIGGER.test(rest) || rest.startsWith("'");

/** Neutralises a cell for a spreadsheet. Applied to every exported cell. */
export function guardCsvCell(cell: string): string {
  return FORMULA_TRIGGER.test(cell) || cell.startsWith("'") ? `'${cell}` : cell;
}

/**
 * Undoes `guardCsvCell`. Applied to every imported cell, including the ones
 * that can never carry a trigger — a uniform rule has no seam to forget, and it
 * is a no-op on everything else.
 */
export function unguardCsvCell(cell: string): string {
  return cell.startsWith("'") && isGuarded(cell.slice(1))
    ? cell.slice(1)
    : cell;
}
